<script lang="ts" context="module">
  import { z } from "zod";

  const GOOGLE_ADS_TYPES: any = {
    billboard: {
      // above the fold: keep the reserved box open on no-fill to avoid CLS
      collapse: false,

      sizes: ["fluid", [300, 250], [728, 90], [970, 250]],

      mapping: [
        [
          [980, 0],
          [
            [728, 90],
            [970, 250],
            [980, 200],
          ],
        ],
        [
          [414, 0],
          ["fluid", [300, 250], [300, 100]],
        ],
        [
          [0, 0],
          ["fluid", [300, 250], [300, 100]],
        ],
      ],
    },

    billboard_small: {
      collapse: false,

      sizes: [
        "fluid",
        [300, 50],
        [300, 100],
        [300, 250],
        [320, 50],
        [320, 480],
        [728, 90],
        [970, 50],
      ],

      mapping: [
        [
          [980, 0],
          [
            [728, 90],
            [930, 100],
            [930, 130],
            [970, 90],
            [970, 250],
            [980, 120],
            [980, 150],
            [980, 200],
          ],
        ],
        [
          [500, 0],
          ["fluid", [320, 100], [320, 50], [300, 100], [300, 50]],
        ],
        [
          [0, 0],
          ["fluid", [320, 100], [320, 50], [300, 100], [300, 50]],
        ],
      ],
    },

    in_article: {
      sizes: [
        "fluid",
        [300, 250],
        [336, 280],
        [580, 400],
        [728, 90],
        [460, 60],
        [234, 60],
      ],

      mapping: [
        [
          [728, 0],
          [
            [728, 90],
            [620, 100],
            [620, 200],
            [940, 830],
            [460, 60],
            [336, 280],
            [234, 60],
          ],
        ],
        [
          [414, 0],
          [
            "fluid",
            [320, 100],
            [300, 250],
            [336, 280],
            [300, 100],
            [460, 60],
            [234, 60],
          ],
        ],
        [
          [0, 0],
          ["fluid", [300, 250], [300, 100], [460, 60], [234, 60]],
        ],
      ],
    },
  };

  export function getAdDivId(id: string): string {
    return `gid-ads-${id.trim().replaceAll("/", "-")}`;
  }

  export const CustomFields = z.object({
    show_in_offensive_content: z.boolean().default(true),
    google_ad: z
      .object({
        adUnitPath: z.string(),
        type: z.enum(Object.keys(GOOGLE_ADS_TYPES)),
        minHeight: z.string().optional(),
      })
      .optional(),
  });
</script>

<script lang="ts">
  import { getContext } from "svelte";
  import Data from "@reperio-news/core.components/blocks/AdPosition/Data";

  const context: any = getContext("context");

  let show = true;

  export let globalId = undefined;
  export let globalSource = undefined;
  export let id: string;
  export let data: z.infer<typeof Data>;

  if (
    data.custom_fields?.show_in_offensive_content !== false &&
    $context.category?.custom_fields?.ads_disabled === true
  ) {
    show = false;
  }

  // always hide when individually set per article
  if ($context.content?.custom_fields?.ads_disabled === true) {
    show = false;
  }

  if (
    ($context.client?.is_mobile && data.for_mobile === false) ||
    (!$context.client?.is_mobile && data.for_desktop === false)
  ) {
    show = false;
  }

  let googleAd: string = undefined;

  if (
    data.custom_fields?.google_ad?.adUnitPath &&
    data.custom_fields?.google_ad?.type &&
    GOOGLE_ADS_TYPES[data.custom_fields.google_ad.type]
  ) {
    googleAd = `<script>
    googletag.cmd.push(function() { 
    googletag.defineSlot(
        '${data.custom_fields.google_ad.adUnitPath}', 
        ${JSON.stringify(
          GOOGLE_ADS_TYPES[data.custom_fields.google_ad.type].sizes
        )}, 
        '${getAdDivId(id)}').defineSizeMapping(${JSON.stringify(
      GOOGLE_ADS_TYPES[data.custom_fields.google_ad.type].mapping
    )})${
      GOOGLE_ADS_TYPES[data.custom_fields.google_ad.type].collapse === false
        ? ".setCollapseEmptyDiv(false)"
        : ""
    }
        .addService(googletag.pubads());
        googletag.display('${getAdDivId(id)}');
     });
  <\/script>`;
  }
</script>

{#if show}
  {#if googleAd}
    <div
      id={getAdDivId(id)}
      data-ad-unit={data.custom_fields.google_ad.adUnitPath}
      class="gads google-ads-{data.custom_fields.google_ad.type}"
      style={data.custom_fields.google_ad.minHeight
        ? "min-height: " + data.custom_fields.google_ad.minHeight
        : ""}
    >
      {@html googleAd}
    </div>
  {:else}
    <slot />
  {/if}
{/if}

<style lang="scss">
  .gads {
    background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' version='1.1' height='20px' width='50px'><text x='0' y='15' fill='silver' font-size='12'>publicitate</text></svg>");
    background-position: center center;
    background-repeat: no-repeat;
  }

  :global {
    /* min-heights follow the tallest creative per GOOGLE_ADS_TYPES mapping row,
       so neither no-fill nor fill moves the content below */
    .google-ads-billboard_small {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 100px;

      @media (min-width: 980px) {
        min-height: 200px;
      }
    }

    .google-ads-billboard {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 250px;
      margin-bottom: 10px;
    }

    .google-ads-in_article {
      display: flex;
      justify-content: center;
      align-items: center;
      min-height: 250px;
      margin-bottom: 10px;

      @media (min-width: 414px) {
        min-height: 280px;
      }
    }
  }
</style>
